// @flow strict
import * as React from 'react';
import generateUID from './uid';
import { VirtualDOMPortal, __DEBUG_STORE__ } from './VirtualDOMPortalProvider';

export type UseVDOMPortalResult = {|
  isOpen: boolean,
  open: () => void,
  close: () => void,
|};

type UseVDOMPortalArgs = UseVDOMPortalResult => React.Node;
type UseVDOMPortalOptions = {|
  onClose?: () => void | Promise<mixed>,
  onOpen?: () => void | Promise<mixed>,
|};

type UseVDOMPortal = (
  factory: UseVDOMPortalArgs,
  options?: UseVDOMPortalOptions,
  inputs?: $ReadOnlyArray<mixed>,
) => UseVDOMPortalResult;

/** A hook which generates a unique ID per component instance */
const useUID = () => {
  const { current: uid } = React.useRef(generateUID());
  return uid;
};

const extractKey = (node: React.Node) =>
  typeof node === 'object' && node !== null && !Array.isArray(node.key) ? node.key : null;

/**
 * A hook which allows you to escape to a place higher in the virtual DOM hierarchy. The element will be "relocated" into the nearest VirtualDOMPortalProvider
 * @param factory - A function which accepts the result of this hook as arguments, and returns the React node to portal
 * @param options - Additional options
 * @param inputs - Optional dependency list to optimize for rerenders why the portal is open. If no input is provided, then the portaled element will rerender whenever this hook rerenders.
 * @returns a VDOMPortal result. The result can reveal the following properties:
 *    @isOpen: If true, the overlay is currently open.
 *    @open: A function to open the portal
 *    @close: A function to close the portal. Every portal'ed element needs to be explicitly closed
 *
 * NOTE: Since the element will be placed in a different place in the hierarchy, contexts and event bubbling will work in respect to the portal location, not at the site of the hook.
 * It may be necessary to forward contexts over to the portal'ed element.
 *
 */
const useVDOMPortal: UseVDOMPortal = (factory, options = {}, inputs) => {
  const id = useUID();
  const { mountNode, unmountNode } = React.useContext(VirtualDOMPortal);
  const [isOpen, setIsOpen] = React.useState(false);
  // A synchronous version of the isOpen React state so we can imperatively query with certainty the eventual open state
  const isOpenRef = React.useRef(false);
  const mountedRef = React.useRef(true);
  const { onOpen, onClose } = options;
  const setIsOpenSafe = React.useCallback(val => {
    if (mountedRef.current) {
      setIsOpen(val);
    }
  }, []);
  const node = React.useRef(null);
  const key = React.useRef(null);

  // Uniquely associate a component type to a component instance per hook
  const mount = React.useCallback(() => {
    const maybePromise = onOpen && onOpen();
    if (typeof maybePromise === 'object') {
      maybePromise.then(() => {
        isOpenRef.current = true;
        mountNode({ node: node.current, id, stateChange: setIsOpenSafe });
      });
    } else {
      isOpenRef.current = true;
      mountNode({ node: node.current, id, stateChange: setIsOpenSafe });
    }
  }, [id, mountNode, setIsOpenSafe, onOpen]);
  const unmount = React.useCallback(() => {
    const maybePromise = onClose && onClose();
    if (typeof maybePromise === 'object') {
      maybePromise.then(() => {
        isOpenRef.current = false;
        unmountNode(id);
      });
    } else {
      isOpenRef.current = false;
      unmountNode(id);
    }
  }, [id, unmountNode, onClose]);

  const result = React.useMemo(
    () => ({
      isOpen,
      open: mount,
      close: unmount,
    }),
    [isOpen, mount, unmount],
  );

  // After each render, make sure the elements within the Portal are kept in sync
  React.useEffect(
    () => {
      const previousKey = extractKey(node.current);
      node.current = factory(result);
      if (isOpenRef.current) {
        // If the external React key did not change, treat it as an upsert
        const upsert = previousKey === extractKey(node.current);
        mountNode({ node: node.current, id, stateChange: setIsOpenSafe }, { upsert });
      }
      // Track the new key, if applicable
      // JSX intrinsics have its keys treated as nulls
      key.current = previousKey;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Array.isArray(inputs) ? [id, result, mountNode, ...inputs] : undefined,
  );

  React.useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  // Effect that runs only in dev to help track orphaned portals
  if (DEBUG) {
    // This is fine, as DEBUG is an immutable value so the ordering of hook execution never changes
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(
      () => () => {
        // When component unmounts, set a timeout in 2 minutes to see if overlay is still open
        setTimeout(() => {
          if (__DEBUG_STORE__.current.find(element => element.id === id)) {
            console.info(`Opened node persisted for more than two minutes after its parent unmounted.
              If the node is supposed to still be open, then ignore this messaging.
              If the node was meant to closed, then it needs to be explicitly closed through the provided close callback. 
              The following potential orphaned nodes were found: [${React.Children.map(
                node.current,
                child => child?.type?.name,
              )
                .filter(Boolean)
                .join(',')}]`);
          }
        }, 1000 * 60 * 2);
      },
      [id],
    );
  }
  return result;
};

export default useVDOMPortal;
